/**
 * What to show a signed-in person, given their firm membership (Phase 22).
 *
 * The database is the authority: with no active firm, every rule returns no
 * rows. This check exists only so that person sees a sentence explaining why,
 * rather than an empty project list and a "New project" button that fails.
 */

import type { MyMembership } from '@/lib/data/firms';
import type { SupportSession } from '@/lib/data/admin';

export type MembershipLookup =
  | {
      ok: true;
      membership: MyMembership | null;
      isPlatformAdmin?: boolean;
      /** An admin's open support session (0033), if any — Phase 26. */
      supportSession?: SupportSession | null;
    }
  | { ok: false };

export type FirmAccess =
  | { kind: 'ok'; membership: MyMembership | null }
  | { kind: 'no-firm' }
  | { kind: 'platform-admin' }
  | { kind: 'support'; session: SupportSession }
  | { kind: 'suspended'; firmName: string };

export function firmAccess(lookup: MembershipLookup): FirmAccess {
  // A failed lookup lets the person in. Locking everyone out because one
  // request failed — or because the app deployed before migration 0030 was
  // applied, and the table doesn't exist yet — is worse than an app whose
  // data rules still apply regardless.
  if (!lookup.ok) return { kind: 'ok', membership: null };
  const m = lookup.membership;
  // An admin account belongs to no firm on purpose (F11); telling it to "ask
  // whoever set up your firm" would be wrong.
  if (!m) {
    if (!lookup.isPlatformAdmin) return { kind: 'no-firm' };
    // An open support session puts the admin into that firm's app, read-only.
    return lookup.supportSession ? { kind: 'support', session: lookup.supportSession } : { kind: 'platform-admin' };
  }
  if (m.firm.status !== 'active') return { kind: 'suspended', firmName: m.firm.name };
  return { kind: 'ok', membership: m };
}

export function firmAccessNotice(
  access: Exclude<FirmAccess, { kind: 'ok' } | { kind: 'support' }>,
): { title: string; body: string } {
  switch (access.kind) {
    case 'no-firm':
      return {
        title: 'No firm yet',
        body: "Your sign-in works, but your account isn't part of a design firm yet. "
          + 'Ask the person who set up your firm to add you.',
      };
    case 'platform-admin':
      return {
        title: 'Platform admin',
        body: 'This account administers Design Studio HQ, so it isn’t part of a design firm. '
          + 'The admin area isn’t available in the app yet.',
      };
    case 'suspended':
      return {
        title: 'Account paused',
        body: `${access.firmName}'s account is paused, so its projects aren't available right now. `
          + 'Please contact Design Studio HQ support.',
      };
  }
}
